import { useMemo } from 'react';

import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import {
  SchemaEvaluatedField,
  SchemaFields,
  SchemaFieldTypesEnum,
} from '@spazious/dollhouse-engine';
import { SchemaFieldUnitTypesEnum } from '@spazious/dollhouse-fsetup';
import { useTranslate } from '@spazious/dollhouse-i18n';
import { Flex, FlexProps } from '@spazious/dollhouse-uikit';

import { BooleanField } from './BooleanField.js';
import { EnumField } from './EnumField.js';
import { NumericField } from './NumericField.js';

const schemaVectorSizes = {
  3: 2,
  4: 3,
  5: 4,
};

const normalValueToDegrees = (value: number) => (360 * value).toFixed(0);
const degreesToNormalValue = (degrees: string) => parseFloat(degrees) / 360;

export interface InspectorAttributesProps extends Omit<FlexProps, 'order'> {
  schemaFields: SchemaEvaluatedField;
  order?: string[];
  fieldsResetSeating?: string[];
  disabled?: boolean;
  hasHiddenNodes?: boolean;
  onValueChange?: () => void;
}

export const sortByTypeAndName = <T extends [string, SchemaFields]>(
  [keyA, valueA]: T,
  [keyB, valueB]: T
) => `${valueA.type}`.localeCompare(`${valueB.type}`) || keyA.localeCompare(keyB);

export const sortByOrder = <T extends string>(a: T, b: T, order: string[]) => {
  const orderA = order.indexOf(a);
  const orderB = order.indexOf(b);

  if (orderA === -1 && orderB !== -1) {
    return 1; // a comes after b
  } else if (orderA !== -1 && orderB === -1) {
    return -1; // a comes before b
  } else if (orderA === -1 && orderB === -1) {
    return 0; // both a and b are not in the order array, sort by name
  }
  return orderA - orderB; // both a and b are in the order array, sort by order
};

export const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export const InspectorAttributes: React.FC<InspectorAttributesProps> = ({
  schemaFields,
  order = [],
  fieldsResetSeating = [],
  disabled,
  hasHiddenNodes,
  onValueChange,
  ...restProps
}) => {
  const translation = useTranslate();
  const fields = useMemo(
    () =>
      Object.entries(schemaFields)
        .sort((a, b) => {
          return sortByOrder(a[0], b[0], order) || sortByTypeAndName(a, b);
        })
        .reduce((acc, [key, settings]) => {
          const {
            type,
            default: defaultValue,
            label,
            visible,
            // eslint-disable-next-line no-unused-vars
            furnitureTypes: _,
            ...restSettings
          } = settings;
          const formattedLabel = capitalize(translation(label) ?? key);
          const resetsSeating = fieldsResetSeating.includes(key);
          if (visible === false) {
            return acc;
          }
          switch (type) {
            case SchemaFieldTypesEnum.Boolean:
              return [
                ...acc,
                <BooleanField
                  key={`attrs.${key}`}
                  defaultValue={defaultValue}
                  disabled={disabled}
                  icon={formattedLabel}
                  meta={CommanderActionTypesEnum.Param}
                  path={`attrs.${key}`}
                  {...restSettings}
                />,
              ];
            case SchemaFieldTypesEnum.Enum:
            case SchemaFieldTypesEnum.Furniture:
              return [
                ...acc,
                <EnumField
                  key={`attrs.${key}`}
                  defaultValue={defaultValue}
                  disabled={disabled}
                  hasHiddenNodes={hasHiddenNodes}
                  icon={formattedLabel}
                  meta={CommanderActionTypesEnum.Param}
                  options={restSettings.options}
                  path={`attrs.${key}`}
                  resetsSeating={resetsSeating}
                  onValueChange={onValueChange}
                  {...restSettings}
                />,
              ];
            case SchemaFieldTypesEnum.Number: {
              const { min, max, step, description, unitType } = restSettings as any;
              if (unitType === SchemaFieldUnitTypesEnum.Rotation) {
                return [
                  ...acc,
                  <NumericField
                    key={`attrs.${key}`}
                    defaultValue={+normalValueToDegrees(defaultValue)}
                    disabled={['size'].includes(key) || disabled}
                    format={normalValueToDegrees}
                    hasHiddenNodes={hasHiddenNodes}
                    icon={formattedLabel}
                    max={+normalValueToDegrees(max)}
                    meta={CommanderActionTypesEnum.Param}
                    min={+normalValueToDegrees(min)}
                    parse={degreesToNormalValue}
                    path={`attrs.${key}`}
                    resetsSeating={resetsSeating}
                    step={+normalValueToDegrees(step)}
                    title={description}
                    unitType={unitType}
                    onValueChange={onValueChange}
                  />,
                ];
              }
              return [
                ...acc,
                <NumericField
                  key={`attrs.${key}`}
                  defaultValue={defaultValue}
                  disabled={['size'].includes(key) || disabled}
                  hasHiddenNodes={hasHiddenNodes}
                  icon={formattedLabel}
                  max={max}
                  meta={CommanderActionTypesEnum.Param}
                  min={min}
                  path={`attrs.${key}`}
                  resetsSeating={resetsSeating}
                  step={step}
                  title={description}
                  unitType={unitType}
                  onValueChange={onValueChange}
                />,
              ];
            }
            case SchemaFieldTypesEnum.Vector2: /*case SchemaFieldTypesEnum.Vector3:
            case SchemaFieldTypesEnum.Vector4: */ {
              const { min, max, step, unitType } = restSettings as any;
              return [
                ...acc,
                <Flex grow justifyContent="space-between">
                  {new Array((schemaVectorSizes as any)[2]).fill(0).map((_, index) => (
                    <NumericField
                      key={`attrs.${key}[${index}]`}
                      defaultValue={defaultValue?.[index]}
                      disabled={disabled}
                      icon={`${formattedLabel}${['X', 'Y', 'Z'][index]}`}
                      max={Array.isArray(max) ? max[index] : max}
                      meta={CommanderActionTypesEnum.Param}
                      min={Array.isArray(min) ? min[index] : min}
                      path={`attrs.${key}[${index}]`}
                      range={false}
                      step={Array.isArray(step) ? step[index] : step}
                      unitType={unitType}
                    />
                  ))}
                </Flex>,
              ];
            }
            default:
              return acc;
          }
        }, [] as React.ReactNode[]),
    [schemaFields, order, translation, disabled, fieldsResetSeating]
  );

  return (
    <Flex column gap="var(--spacing-padding)" {...restProps}>
      {fields}
    </Flex>
  );
};
